<template>
  <app-page-wrapper-grid>
    <div v-if="userData" class="iw-layout-grid__row iw-grid__row__16 profile-info">
      <div class="iw-layout-grid__col--span-12">
        <back-button />
      </div>
      <div class="iw-layout-grid__col--span-4 iw-layout-grid__col--offset-4-desktop">
        <form class="profile-info__form">
          <h1 class="m-t-10 m-b-40 iw-page-title profile-info__title">Edit Profile</h1>
          <template v-if="isFormSubmitted && (v$.$invalid || responseWithErrors)">
            <app-notifier v-for="(message, index) in errors" :key="index" type="failed" :data="message" />
          </template>
          <div class="iw-body-text-small m-l-auto">*Required Information</div>
          <app-input
            ref="email"
            v-model="v$.personalInfo.email.$model"
            :type="'text'"
            :label="'Email'"
            :placeholder="'Enter Email Address'"
            :has-error="v$.personalInfo.email.$error && isFormSubmitted"
            class="iw-body-text-small"
            :error-message="getErrorMessage('email')"
            required
          />
          <span v-if="!isEditPassword" class="iw-body-text-small">Password</span>
          <span v-if="!isEditPassword" class="m-t-15 change-password">
            <span class="iw-body-text-small">******</span>
            <button class="change-password__button iw-hyperlink" @click="enableChangePassword">Change Password</button>
          </span>
          <app-input
            v-if="isEditPassword"
            ref="newPassword"
            v-model="v$.personalInfo.newPassword.$model"
            label="New Password"
            type="password"
            :has-error="(v$.personalInfo.newPassword.$error || unmatchingPasswords) && isFormSubmitted"
            required
          />
          <app-input
            v-if="isEditPassword"
            ref="confirmNew"
            v-model="v$.confirmNew.$model"
            label="Confirm New Password"
            type="password"
            :error-message="confirmNewErrorMessage"
            :has-error="(v$.confirmNew.$error || unmatchingPasswords) && isFormSubmitted"
            class="iw-body-text-small"
            required
          />
          <hr class="divider" />
          <app-input
            ref="firstName"
            v-model="v$.personalInfo.firstName.$model"
            :type="'text'"
            :label="'First Name'"
            :placeholder="'Enter First Name'"
            :has-error="v$.personalInfo.firstName.$error && isFormSubmitted"
            class="iw-body-text-small"
            :error-message="getErrorMessage('firstName')"
            required
          />
          <app-input
            ref="lastName"
            v-model="v$.personalInfo.lastName.$model"
            :type="'text'"
            :label="'Last Name'"
            :placeholder="'Enter Last Name'"
            :has-error="v$.personalInfo.lastName.$error && isFormSubmitted"
            class="iw-body-text-small"
            :error-message="getErrorMessage('lastName')"
            required
          />

          <app-input
            ref="phone"
            v-model="v$.personalInfo.phone.$model"
            :type="'text'"
            :label="'Phone'"
            :placeholder="'Enter Phone number'"
            mask="(###) ###-####"
            :has-error="v$.personalInfo.phone.$error && isFormSubmitted"
            class="iw-body-text-small"
            :error-message="getErrorMessage('phone')"
            required
          />

          <app-button class="submit-button" :text="'Save Changes'" :aria-label="'Save Changes'" :handler="submitProfileForm" />
        </form>
      </div>
    </div>
  </app-page-wrapper-grid>
</template>

<!-- prettier-ignore -->
<script>

import { mapGetters, mapActions } from "vuex";
import useVuelidate from "@vuelidate/core";
import {
  requiredValidation,
  emailValidation,
  ROUTE_NAMES,
  phoneValidations,
  passwordValidations,
  INVALID_PASSWORD_ERR_MSG,
  DIFF_PASSWORD_ERR_MSG
} from "@/utils";
import AppPageWrapperGrid from "@/components/base/app-page-wrapper-grid/AppPageWrapperGrid.vue";
import BackButton from "@/components/buttons/back-button/BackButton.vue";
import PersonalInfoForm from "@/models/personal-info-form";

export default {
  name: "UserPersonalInformation",
  components: {
    AppPageWrapperGrid,
    BackButton
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      isEditPassword: false,
      isFormSubmitted: false,
      responseWithErrors: false,
      personalInfo: undefined,
      confirmNew: "",
      errors: [],
      passwordError: "",
      validatorsCollection: new Map()
    };
  },
  validations() {
    if (!this.isEditPassword) {
      return {
        personalInfo: {
          firstName: requiredValidation(),
          lastName: requiredValidation(),
          email: emailValidation(),
          phone: phoneValidations(),
          newPassword: {}
        },
        confirmNew: {}
      };
    } else {
      return {
        personalInfo: {
          firstName: requiredValidation(),
          lastName: requiredValidation(),
          email: emailValidation(),
          phone: phoneValidations(),
          newPassword: passwordValidations()
        },
        confirmNew: passwordValidations()
      };
    }
  },
  computed: {
    ...mapGetters("user", {
      userData: "getUserData"
    }),
    confirmNewErrorMessage() {
      if ((this.v$.personalInfo.newPassword.$error || this.v$.confirmNew.$error) && this.isFormSubmitted) {
        return INVALID_PASSWORD_ERR_MSG;
      }
      if (!this.isConfirmNewValid() && this.isFormSubmitted) {
        return DIFF_PASSWORD_ERR_MSG;
      }
      return "";
    },
    unmatchingPasswords() {
      return !this.v$.personalInfo.newPassword.$error && !this.v$.confirmNew.$error && !this.isConfirmNewValid();
    }
  },
  methods: {
    ...mapActions("user", ["updateUserInfo"]),
    async submitProfileForm() {
      this.isFormSubmitted = true;
      this.responseWithErrors = false;
      this.v$.$touch();

      if (!this.v$.$invalid && (!this.isEditPassword || (this.isConfirmNewValid() && this.isNewPasswordNotEmpty()))) {
        try {
          if (!this.isEditPassword) this.personalInfo.newPassword = "";
          this.passwordError = "";
          await this.updateUserInfo(this.personalInfo);
          this.isEditPassword = false;
          this.personalInfo.newPassword = "";
          this.confirmNew = "";
          return this.personalInfo;
        } catch (e) {
          this.responseWithErrors = true;
          if (e === 408) {
            this.errors = ["Your account cannot be reached at this time. Please try again."];
          } else if (e === 409) {
            this.errors = ["This email address or phone number is already associated with another account."];
          } else {
            this.errors = ["Your account cannot be updated at this time. Please try again later."];
          }
        }
      } else {
        this.setFocusOnFirstError();
      }
    },
    setFocusOnFirstError() {
      let focusSet = false;
      //set focus for validatorsCollection elements
      this.validatorsCollection.forEach((item, key) => {
        if (item.validator.$invalid && !focusSet) {
          this.$refs[key].$el?.focus();
          focusSet = true;
        }
      });

      //set focus for password elements only when editting password and we don't have an error in the first input (email)
      //we can't push password validations in to validatorsCollection because these are conditionally initialized
      if (this.isEditPassword && !this.v$.personalInfo.email.$error) {
        if (this.v$.personalInfo.newPassword.$error) {
          this.$refs.newPassword.$el?.focus();
          return;
        }
        if (this.v$.confirmNew.$error) {
          this.$refs.confirmNew.$el?.focus();
          return;
        }
        if (!this.isConfirmNewValid()) {
          this.$refs.newPassword.$el?.focus();
        }
      }
    },
    enableChangePassword() {
      this.isEditPassword = true;
      this.isFormSubmitted = false;
    },
    isNewPasswordNotEmpty() {
      return this.personalInfo.newPassword !== "";
    },
    isConfirmNewValid() {
      return this.confirmNew === this.personalInfo.newPassword;
    },
    getErrorMessage(prop) {
      return this.v$.personalInfo[prop].$error && this.isFormSubmitted ? this.validatorsCollection.get(prop).errorMessage : "";
    }
  },
  watch: {
    userData() {
      if (!this.userData)
        this.$router.push({
          name: ROUTE_NAMES.VENDOR_PICKER
        });
    }
  },
  mounted() {
    this.personalInfo = new PersonalInfoForm(this.userData);

    //Set validators in the same order inputs are displayed in the form will allow to set focus to the first invalid field
    //map keys and element refs must match.
    //elements conditionally rendered should not be in this collection
    this.validatorsCollection
      .set("email", {
        validator: this.v$.personalInfo.email,
        errorMessage: "Please enter a valid email address"
      })
      .set("firstName", {
        validator: this.v$.personalInfo.firstName,
        errorMessage: "Please enter your first name"
      })
      .set("lastName", {
        validator: this.v$.personalInfo.lastName,
        errorMessage: "Please enter your last name"
      })
      .set("phone", {
        validator: this.v$.personalInfo.phone,
        errorMessage: "Please enter a valid 10-digit mobile number"
      });
  }
};
</script>

<style scoped lang="scss">
.dark {
  .profile-info__form {
    background-color: $background-dark--2;
  }
}

.profile-info {
  &__title {
    width: 100%;
    text-align: left;
    margin-bottom: 29px;
    @include respond-to-mobile {
      margin-bottom: 19px;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    width: 100%;
    border-radius: 5px;
  }
}
.change-password {
  display: flex;
  justify-content: space-between;
  padding: 0px 16px;

  &__button {
    border: none;
    background-color: transparent;
    cursor: pointer;
  }
}

.divider {
  border-top: 1px gray;
  width: calc(100% - 2px);
}

.submit-button {
  margin: 33px 0 48px;
  width: 100%;
}
</style>
